import { ColumnFiltersState } from "@tanstack/react-table";
import type { DefaultsPlan } from "@/providers/legislation-filter/hooks/useLegislationFilterConfig";
import { DATASOURCE_FILTER_ID } from "./datasourceFilter";

/**
 * The filter state a tenant's predefined ("standard") filters describe: the
 * source they belong to plus that source's default child selections.
 */
export function buildDefaultColumnFilters(
  defaultsPlan: DefaultsPlan
): ColumnFiltersState {
  if (!defaultsPlan) return [];

  return [
    { id: DATASOURCE_FILTER_ID, value: defaultsPlan.sourceKey },
    ...defaultsPlan.childFilters.map(({ id, value }) => ({ id, value })),
  ];
}

/**
 * A filter left with nothing selected constrains nothing, and the faceted
 * filters drop such an entry rather than storing an empty one — so an empty
 * value must not make an otherwise identical selection look different.
 */
const isEmptySelection = (value: unknown): boolean =>
  value == null || (Array.isArray(value) && value.length === 0);

const canonicalizeValue = (value: unknown): string =>
  JSON.stringify(Array.isArray(value) ? [...value].map(String).sort() : value);

/** Selection order is a UI accident, so compare canonically rather than by identity. */
const canonicalize = (columnFilters: ColumnFiltersState): string =>
  columnFilters
    .filter((filter) => !isEmptySelection(filter.value))
    .map(({ id, value }) => `${id}=${canonicalizeValue(value)}`)
    .sort()
    .join("|");

/**
 * Whether `columnFilters` already *is* the predefined selection — the applied
 * state the "Standardfiltre" action would produce, which is when offering it
 * would do nothing.
 */
export function areDefaultColumnFiltersApplied(
  columnFilters: ColumnFiltersState,
  defaultsPlan: DefaultsPlan
): boolean {
  if (!defaultsPlan) return false;

  return (
    canonicalize(columnFilters) ===
    canonicalize(buildDefaultColumnFilters(defaultsPlan))
  );
}

/**
 * `columnFilters` with the predefined selections of the source it is scoped to
 * applied over whatever those filters currently hold, leaving every other
 * filter alone. Choosing a source is what asks for this — the source's own
 * standard selections — so it is not the same as
 * {@link buildDefaultColumnFilters}, which replaces the whole selection.
 *
 * Returns the given state unchanged when they are already in place, so a caller
 * can avoid a pointless write.
 */
export function withSourceDefaultFilters(
  columnFilters: ColumnFiltersState,
  defaultsPlan: DefaultsPlan
): ColumnFiltersState {
  if (!defaultsPlan) return columnFilters;

  const planFilterIds = new Set(defaultsPlan.childFilters.map(({ id }) => id));
  const alreadyApplied = defaultsPlan.childFilters.every((child) =>
    columnFilters.some(
      (filter) =>
        filter.id === child.id &&
        canonicalizeValue(filter.value) === canonicalizeValue(child.value)
    )
  );

  if (alreadyApplied) return columnFilters;

  return [
    ...columnFilters.filter((filter) => !planFilterIds.has(filter.id)),
    ...defaultsPlan.childFilters.map(({ id, value }) => ({ id, value })),
  ];
}
